import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

const STEPS = ['name', 'background', 'frame-size', 'upload'];

const CANONICAL_OPTIONS = {
  background: [
    { key: 'light', label: 'Light', aliases: ['light'] },
    { key: 'dark', label: 'Dark', aliases: ['dark'] },
  ],
  frame: [
    { key: 'black', label: 'Black', aliases: ['black', 'black frame'] },
    { key: 'white', label: 'White', aliases: ['white', 'white frame'] },
    { key: 'natural', label: 'Natural', aliases: ['natural', 'natural frame'] },
    { key: 'dark-brown', label: 'Dark Brown', aliases: ['dark brown', 'dark-brown', 'darkbrown'] },
    { key: 'print-only', label: 'Print Only', aliases: ['print only', 'unframed', 'none'] },
  ],
  size: [
    { key: '8x10', label: '8″ × 10″', aliases: ['8×10', '8x10', '8 x 10', '8″×10″', '8”×10”', '8"×10"', '8" x 10"', '8″ x 10″', '8” x 10”'], secondary: '20×25 cm' },
    { key: '12x16', label: '12″ × 16″', aliases: ['12×16', '12x16', '12 x 16', '12″×16″', '12”×16”', '12"×16"', '12" x 16"', '12″ x 16″', '12” x 16”'], secondary: '30×40 cm' },
    { key: '18x24', label: '18″ × 24″', aliases: ['18×24', '18x24', '18 x 24', '18″×24″', '18”×24”', '18"×24"', '18" x 24"', '18″ x 24″', '18” x 24”'], secondary: '45×60 cm' },
  ],
};

class ProductCustomizerComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.initialized) return;
    this.initialized = true;

    this.config = this.#parseConfig();
    this.catalog = this.#buildCatalog(this.config);

    if (this.catalog.variants.length === 0) return;

    this.dom = this.#cacheDom();
    this.prefill = this.#getPrefillFromQuery();
    this.state = this.#createInitialState();
    this.visibleStepCount = 1;
    this.activeStepIndex = 0;
    this.resumeStepIndex = null;
    this.isSubmitting = false;
    this.currentPreviewUrl = null;
    this.hasPreviewVariantInteraction = Boolean(this.prefill?.isEditing);
    this.pendingStepScrollTimeout = null;
    this.trackingState = {
      started: false,
      completedSteps: new Set(),
      seenBlockers: new Set(),
      addToCartClicked: false,
      customizerCompleted: false,
      lastUploadedSignature: '',
    };

    if (this.prefill?.isEditing) {
      this.visibleStepCount = STEPS.length;
      this.state.completedSteps = new Set(['name', 'background', 'frame-size']);
      this.activeStepIndex = this.#hasExistingUpload() ? STEPS.length - 1 : 3;

      if (this.#hasExistingUpload()) {
        this.state.completedSteps.add('upload');
      }
    }

    this.#bindEvents();
    this.#bindResponsivePreviewPlacement();
    this.#bindLaunchCountdown();
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unbindResponsivePreviewPlacement();
    this.#unbindLaunchCountdown();
    this.#revokePreviewObjectUrl();
  }

  #parseConfig() {
    const configNode = this.querySelector('[data-customizer-config]');
    if (!(configNode instanceof HTMLScriptElement)) {
      throw new Error('Customizer config is missing');
    }

    return JSON.parse(configNode.textContent || '{}');
  }

  #cacheDom() {
    return {
      scrollContainer: this.querySelector('[data-scroll-container]'),
      shell: this.querySelector('.product-customizer__shell'),
      mediaColumn: this.querySelector('.product-customizer__media-column'),
      panelColumn: this.querySelector('.product-customizer__panel-column'),
      intro: this.querySelector('.product-customizer__intro'),
      progress: this.querySelector('.product-customizer__progress'),
      introHeading: this.querySelector('[data-intro-heading]'),
      currentSelectionPrice: this.querySelector('[data-current-selection-price]'),
      currentSelectionOffer: this.querySelector('[data-current-selection-offer]'),
      currentSelectionComparePrice: this.querySelector('[data-current-selection-compare-price]'),
      currentSelectionSavings: this.querySelector('[data-current-selection-savings]'),
      currentSelectionConfig: this.querySelector('[data-current-selection-config]'),
      launchOfferLabels: [...this.querySelectorAll('[data-launch-offer-label]')],
      launchCountdownWraps: [...this.querySelectorAll('[data-launch-countdown-wrap]')],
      launchCountdowns: [...this.querySelectorAll('[data-launch-countdown]')],
      stickySelectionOffer: this.querySelector('[data-sticky-selection-offer]'),
      stickySelectionComparePrice: this.querySelector('[data-sticky-selection-compare-price]'),
      stickySelectionSavings: this.querySelector('[data-sticky-selection-savings]'),
      stickySelectionConfig: this.querySelector('[data-sticky-selection-config]'),
      previewImage: this.querySelector('[data-preview-image]'),
      progressLabel: this.querySelector('[data-progress-label]'),
      progressDots: [...this.querySelectorAll('[data-progress-dot]')],
      steps: [...this.querySelectorAll('[data-step-key]')],
      nameInput: this.querySelector('[data-name-input]'),
      uploadHeading: this.querySelector('[data-upload-heading]'),
      uploadHeadingStatus: this.querySelector('[data-upload-heading-status]'),
      uploadInput: this.querySelector('[data-upload-input]'),
      uploadTrigger: this.querySelector('[data-upload-trigger]'),
      uploadEmpty: this.querySelector('[data-upload-empty]'),
      uploadFilled: this.querySelector('[data-upload-filled]'),
      uploadPreviewFallback: this.querySelector('[data-upload-preview-fallback]'),
      uploadPreviewImage: this.querySelector('[data-upload-preview-image]'),
      uploadFileName: this.querySelector('[data-upload-file-name]'),
      uploadWarning: this.querySelector('[data-upload-warning]'),
      ctaButton: this.querySelector('[data-primary-cta]'),
      totalPrice: this.querySelector('[data-total-price]'),
      shippingPrimary: this.querySelector('[data-shipping-primary]'),
      shippingSecondary: this.querySelector('[data-shipping-secondary]'),
      submitError: this.querySelector('[data-submit-error]'),
      liveRegion: this.querySelector('[data-live-region]'),
      summaryValues: {
        name: this.querySelector('[data-summary-value="name"]'),
        background: this.querySelector('[data-summary-value="background"]'),
        frameSize: this.querySelector('[data-summary-value="frame-size"]'),
        upload: this.querySelector('[data-summary-value="upload"]'),
        addons: this.querySelector('[data-summary-value="addons"]'),
      },
      summarySwatches: {
        background: this.querySelector('[data-summary-swatch="background"]'),
        frame: this.querySelector('[data-summary-swatch="frame"]'),
      },
      summaryBadges: {
        framePopular: this.querySelector('[data-summary-badge="frame-popular"]'),
      },
      errors: {
        name: this.querySelector('[data-error-for="name"]'),
        upload: this.querySelector('[data-error-for="upload"]'),
      },
      sizePrices: [...this.querySelectorAll('[data-size-price]')],
      addonPrices: [...this.querySelectorAll('[data-addon-price]')],
      optionButtons: [...this.querySelectorAll('[data-option-button]')],
      addonButtons: [...this.querySelectorAll('[data-addon-key]')],
      addonFreeShippingBadges: [...this.querySelectorAll('[data-addon-free-shipping-badge]')],
    };
  }

  #buildCatalog(config) {
    const choiceMaps = {
      background: this.#resolveChoices(config.variants, 'background'),
      frame: this.#resolveChoices(config.variants, 'frame'),
      size: this.#resolveChoices(config.variants, 'size'),
    };

    const normalizedVariants = config.variants.map((variant) => ({
      id: variant.id,
      available: Boolean(variant.available),
      price: Number(variant.price),
      compareAtPrice: Number(variant.compareAtPrice || 0),
      title: variant.title,
      sizeKey: this.#toCanonicalKey('size', variant.size),
      frameKey: this.#toCanonicalKey('frame', variant.frame),
      backgroundKey: this.#toCanonicalKey('background', variant.background),
      sizeLabel: variant.size,
      frameLabel: variant.frame,
      backgroundLabel: variant.background,
      image: variant.image || null,
    }));

    const previewMap = {};
    normalizedVariants.forEach((variant) => {
      const previewKey = `${variant.frameKey}::${variant.backgroundKey}`;
      const current = previewMap[previewKey];
      const nextScore = this.#previewVariantScore(variant);
      const currentScore = current ? this.#previewVariantScore(current) : -1;

      if (!current || nextScore > currentScore) {
        previewMap[previewKey] = variant;
      }
    });

    return {
      choiceMaps,
      variants: normalizedVariants,
      previewMap,
      addOns: {
        rush: {
          key: 'rush',
          label: config.addOns?.rush?.label || 'Priority Preview',
          variantId: config.addOns?.rush?.variantId || null,
          price: Number(config.addOns?.rush?.price || 999),
          compareAtPrice: Number(config.addOns?.rush?.compareAtPrice || 0),
        },
        digital: {
          key: 'digital',
          label: config.addOns?.digital?.label || 'Digital copy',
          variantId: config.addOns?.digital?.variantId || null,
          price: Number(config.addOns?.digital?.price || 1299),
          compareAtPrice: Number(config.addOns?.digital?.compareAtPrice || 0),
        },
      },
    };
  }

  #resolveChoices(variants, type) {
    const definitions = CANONICAL_OPTIONS[type];
    const resolved = {};

    definitions.forEach((definition) => {
      const match = variants.find((variant) => {
        const value = variant[type];
        return this.#matchesAliases(value, definition.aliases);
      });

      resolved[definition.key] = {
        ...definition,
        actualValue: match?.[type] || definition.label,
      };
    });

    return resolved;
  }

  #createInitialState() {
    const defaultSizeKey = this.#toCanonicalKey('size', this.config.defaultSelections?.size) || '12x16';
    const defaultFrameKey = this.#toCanonicalKey('frame', this.config.defaultSelections?.frame) || 'black';
    const defaultBackgroundKey = this.#toCanonicalKey('background', this.config.defaultSelections?.background) || 'light';

    const preferredVariant = this.catalog.variants.find(
      (variant) =>
        variant.sizeKey === defaultSizeKey &&
        variant.frameKey === defaultFrameKey &&
        variant.backgroundKey === defaultBackgroundKey
    );

    const variant = preferredVariant || this.catalog.variants[0];
    const prefill = this.prefill;
    const fallbackSizeKey = variant?.sizeKey || defaultSizeKey || '12x16';
    const fallbackFrameKey = variant?.frameKey || defaultFrameKey || 'black';
    const fallbackBackgroundKey = variant?.backgroundKey || defaultBackgroundKey || 'light';

    return {
      selections: {
        size: this.#resolveSelectionKey('size', prefill?.selections?.size, fallbackSizeKey),
        frame: this.#resolveSelectionKey('frame', prefill?.selections?.frame, fallbackFrameKey),
        background: this.#resolveSelectionKey('background', prefill?.selections?.background, fallbackBackgroundKey),
      },
      completedSteps: new Set(),
      name: prefill?.name || '',
      upload: {
        file: null,
        fileName: prefill?.upload?.fileName || '',
        photoId: prefill?.upload?.photoId || '',
        mimeType: '',
        previewUrl: prefill?.upload?.previewUrl || '',
        uploadedUrl: prefill?.upload?.uploadedUrl || '',
        localObjectUrl: '',
        warning: '',
        warningCode: '',
        error: '',
        errorCode: '',
        isUploading: false,
        uploadFailed: false,
      },
      addOns: {
        rush: false,
        digital: false,
      },
    };
  }

  #getCopy(key, fallback = '') {
    const value = this.config?.copy?.[key];
    return typeof value === 'string' && value.trim() ? value : fallback;
  }

  #getPrefillFromQuery() {
    if (typeof window === 'undefined') return null;

    const params = new URLSearchParams(window.location.search);
    if (params.get('customizer_edit') !== '1') {
      return null;
    }

    const name = params.get('pc_name') || '';
    const background = this.#toCanonicalKey('background', params.get('pc_background') || '');
    const frame = this.#toCanonicalKey('frame', params.get('pc_frame') || '');
    const size = this.#toCanonicalKey('size', params.get('pc_size') || '');
    const photoPreview = params.get('pc_photo_preview') || '';
    const photoReference = params.get('pc_photo_reference') || '';
    const photoId = params.get('pc_photo_id') || '';
    const uploadStatus = params.get('pc_upload_status') || '';
    const cartItemKey = params.get('cart_item_key') || params.get('pc_line_key') || '';
    const cartVariantId = params.get('cart_variant_id') || params.get('pc_variant_id') || '';
    const customizationId = params.get('tf_customization_id') || params.get('pc_customization_id') || photoId || '';
    const previewUrl = photoPreview.includes('://') ? photoPreview : '';
    const uploadedUrl = photoReference.includes('://') ? photoReference : previewUrl;

    return {
      isEditing: true,
      cartItemKey,
      cartVariantId,
      customizationId,
      name,
      selections: {
        background: background || null,
        frame: frame || null,
        size: size || null,
      },
      addOns: {
        rush: false,
        digital: false,
      },
      upload: {
        previewUrl,
        uploadedUrl,
        photoId,
        fileName: uploadStatus === 'local' ? photoReference : '',
      },
    };
  }

  #bindEvents() {
    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const editStepButton = target.closest('[data-edit-step]');
      if (editStepButton instanceof HTMLElement) {
        const editStepIndex = Number(editStepButton.dataset.editStep);
        this.#beginStepEdit(editStepIndex);
        this.#render();
        this.#scrollStepIntoView(this.activeStepIndex);
        return;
      }

      const optionButton = target.closest('[data-option-button]');
      if (optionButton instanceof HTMLElement) {
        this.#handleOptionSelection(optionButton);
        return;
      }

      const addonButton = target.closest('[data-addon-key]');
      if (addonButton instanceof HTMLElement) {
        this.#handleAddonToggle(addonButton.dataset.addonKey);
        return;
      }

      if (target.closest('[data-primary-cta]')) {
        void this.#handlePrimaryAction();
        return;
      }

      if (target.closest('[data-upload-trigger]')) {
        event.preventDefault();
        this.dom.uploadInput?.click();
      }
    });

    this.dom.nameInput?.addEventListener('input', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      this.state.name = input.value;
      if (input.value.trim()) {
        this.#clearError('name');
      }
      this.#renderUploadHeading();
      this.#renderSummaries();
    });

    this.dom.uploadInput?.addEventListener('change', async (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      const file = input.files?.[0];
      if (!file) return;
      await this.#handleUpload(file);
    });

    this.dom.uploadTrigger?.addEventListener('dragover', (event) => {
      event.preventDefault();
      this.dom.uploadTrigger?.classList.add('is-dragging');
    });

    this.dom.uploadTrigger?.addEventListener('dragleave', () => {
      this.dom.uploadTrigger?.classList.remove('is-dragging');
    });

    this.dom.uploadTrigger?.addEventListener('drop', async (event) => {
      event.preventDefault();
      this.dom.uploadTrigger?.classList.remove('is-dragging');

      const file = event.dataTransfer?.files?.[0];
      if (!file) return;

      await this.#handleUpload(file);
    });

    this.addEventListener('keydown', (event) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (!target?.matches('[data-option-button]')) return;

      const group = target.dataset.optionGroup;
      if (!group) return;

      if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(event.key)) return;

      const buttons = this.dom.optionButtons.filter((button) => button.dataset.optionGroup === group);
      const currentIndex = buttons.indexOf(target);
      if (currentIndex < 0) return;

      event.preventDefault();

      const delta = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = (currentIndex + delta + buttons.length) % buttons.length;
      buttons[nextIndex]?.focus();
      buttons[nextIndex]?.click();
    });
  }

  #bindResponsivePreviewPlacement() {
    if (typeof window === 'undefined') return;

    this.previewPlacementMediaQuery = window.matchMedia('(max-width: 989px)');
    this.previewPlacementListener = () => this.#syncPreviewPlacement();

    if (typeof this.previewPlacementMediaQuery.addEventListener === 'function') {
      this.previewPlacementMediaQuery.addEventListener('change', this.previewPlacementListener);
    } else if (typeof this.previewPlacementMediaQuery.addListener === 'function') {
      this.previewPlacementMediaQuery.addListener(this.previewPlacementListener);
    }

    this.#syncPreviewPlacement();
  }

  #unbindResponsivePreviewPlacement() {
    if (!this.previewPlacementMediaQuery || !this.previewPlacementListener) return;

    if (typeof this.previewPlacementMediaQuery.removeEventListener === 'function') {
      this.previewPlacementMediaQuery.removeEventListener('change', this.previewPlacementListener);
    } else if (typeof this.previewPlacementMediaQuery.removeListener === 'function') {
      this.previewPlacementMediaQuery.removeListener(this.previewPlacementListener);
    }
  }

  #syncPreviewPlacement() {
    const { shell, mediaColumn, panelColumn, scrollContainer, intro, progress } = this.dom || {};
    if (!(mediaColumn instanceof HTMLElement)) return;

    const isMobile =
      this.previewPlacementMediaQuery?.matches ??
      (typeof window !== 'undefined' && window.matchMedia('(max-width: 989px)').matches);

    if (isMobile) {
      if (!(scrollContainer instanceof HTMLElement) || !(intro instanceof HTMLElement)) return;

      const anchor = progress instanceof HTMLElement ? progress : intro.nextElementSibling;
      if (mediaColumn.parentElement !== scrollContainer || mediaColumn.previousElementSibling !== intro) {
        scrollContainer.insertBefore(mediaColumn, anchor);
      }

      mediaColumn.classList.add('is-mobile-preview');
      return;
    }

    if (!(shell instanceof HTMLElement) || !(panelColumn instanceof HTMLElement)) return;

    if (mediaColumn.parentElement !== shell || mediaColumn.nextElementSibling !== panelColumn) {
      shell.insertBefore(mediaColumn, panelColumn);
    }

    mediaColumn.classList.remove('is-mobile-preview');
  }

  #bindLaunchCountdown() {
    this.#renderLaunchOfferText();
    this.#renderLaunchCountdown();

    if (!this.#getLaunchCountdownEndTimestamp()) return;

    this.launchCountdownTimer = window.setInterval(() => {
      this.#renderLaunchCountdown();
    }, 1000);
  }

  #unbindLaunchCountdown() {
    if (!this.launchCountdownTimer) return;

    window.clearInterval(this.launchCountdownTimer);
    this.launchCountdownTimer = null;
  }

  #renderLaunchOfferText() {
    const offerText = String(this.config.launchOfferText || 'Launch Offer').trim() || 'Launch Offer';

    this.dom.launchOfferLabels.forEach((element) => {
      element.textContent = offerText;
    });
  }

  #renderLaunchCountdown() {
    const endsAt = this.#getLaunchCountdownEndTimestamp();
    const countdownText = endsAt ? this.#formatLaunchCountdown(endsAt - Date.now()) : '';

    this.dom.launchCountdowns.forEach((element) => {
      element.textContent = countdownText;
    });

    this.dom.launchCountdownWraps.forEach((element) => {
      element.hidden = !countdownText;
    });
  }

  #getLaunchCountdownEndTimestamp() {
    const rawValue = String(this.config.launchCountdownEndsAt || '').trim();
    if (!rawValue) return 0;

    const timestamp = Date.parse(rawValue);
    return Number.isFinite(timestamp) ? timestamp : 0;
  }

  #formatLaunchCountdown(milliseconds) {
    if (milliseconds <= 0) {
      return String(this.config.launchCountdownExpiredText || '').trim();
    }

    const totalSeconds = Math.floor(milliseconds / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const prefix = String(this.config.launchCountdownPrefix || '').trim();
    const pad = (value) => String(value).padStart(2, '0');
    const timer = days > 0 ? `${days}d ${pad(hours)}h ${pad(minutes)}m` : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

    return prefix ? `${prefix} ${timer}` : timer;
  }

  #handleOptionSelection(button) {
    const group = button.dataset.optionGroup;
    const key = button.dataset.optionKey;
    if (!group || !key) return;

    if (group === 'background' || group === 'frame' || group === 'size') {
      this.state.selections[group] = key;
      this.hasPreviewVariantInteraction = true;
      this.#clearSubmitError();
      this.#render();
    }
  }

  #handleAddonToggle(key) {
    if (!(key in this.state.addOns)) return;
    this.state.addOns[key] = !this.state.addOns[key];
    this.#clearSubmitError();
    this.#render();
  }

  async #handlePrimaryAction() {
    if (this.isSubmitting) return;

    const stepKey = STEPS[this.activeStepIndex];
    const stepNumber = this.activeStepIndex + 1;
    const validation = await this.#validateStep(stepKey);

    if (!validation.valid) {
      this.#focusAndScrollErrorField(validation.field);
      this.#announce(validation.message);
      return;
    }

    if (!this.trackingState.started) {
      this.trackingState.started = true;
      this.#trackEvent('customizer_started', {
        step_name: stepKey,
        step_number: stepNumber,
      });
    }

    if (!this.trackingState.completedSteps.has(stepKey)) {
      this.trackingState.completedSteps.add(stepKey);
      this.#trackEvent('customizer_step_completed', {
        step_name: stepKey,
        step_number: stepNumber,
      });
    }

    this.state.completedSteps.add(stepKey);

    if (this.activeStepIndex < STEPS.length - 1) {
      this.#advanceToNextStep();
      this.#render();
      this.#scrollStepIntoView(this.activeStepIndex);
      return;
    }

    await this.#submit();
  }

  async #validateStep(stepKey) {
    if (stepKey === 'name') {
      const isValid = Boolean(this.state.name.trim());
      if (!isValid) {
        const message = 'Please enter your dog’s name';
        this.#setError('name', message, 'required', stepKey);
        return { valid: false, message, field: 'name' };
      }

      this.#clearError('name');
    }

    if (stepKey === 'upload') {
      if (!this.#hasExistingUpload()) {
        const message = this.#getCopy('uploadRequiredErrorText', 'Please upload your dog’s photo to continue');
        this.#setError('upload', message, 'required', stepKey);
        return { valid: false, message, field: 'upload' };
      }

      if (this.state.upload.error) {
        this.#setError('upload', this.state.upload.error, this.state.upload.errorCode || 'invalid_upload', stepKey);
        return { valid: false, message: this.state.upload.error, field: 'upload' };
      }

      if (this.state.upload.isUploading) {
        const message = this.#getCopy('uploadInProgressErrorText', 'We’re still uploading your photo — please wait a moment');
        this.#setError('upload', message, 'upload_in_progress', stepKey);
        return { valid: false, message, field: 'upload' };
      }

      if (this.state.upload.uploadFailed) {
        const message = this.#getCopy('uploadFailedErrorText', 'We couldn’t upload your photo right now — please try again');
        this.#setError('upload', message, 'upload_failed', stepKey);
        return { valid: false, message, field: 'upload' };
      }

      this.#clearError('upload');
    }

    return { valid: true };
  }

  #focusAndScrollErrorField(field) {
    const target = field === 'upload' ? this.dom.uploadTrigger : this.dom.nameInput;
    if (!(target instanceof HTMLElement)) return;

    target.focus({ preventScroll: true });
    this.#scrollElementIntoViewCentered(target);
  }

  async #handleUpload(file) {
    this.#clearError('upload');
    this.#clearSubmitError();
    this.#revokePreviewObjectUrl();

    const validationResult = this.#validateFile(file);
    if (validationResult) {
      this.state.upload = {
        file: null,
        fileName: '',
        photoId: '',
        mimeType: '',
        previewUrl: '',
        uploadedUrl: '',
        localObjectUrl: '',
        warning: '',
        warningCode: '',
        error: validationResult.message,
        errorCode: validationResult.code,
        isUploading: false,
        uploadFailed: false,
      };
      this.#setError('upload', validationResult.message, validationResult.code, 'upload');
      this.#render();
      return;
    }

    const localObjectUrl = URL.createObjectURL(file);
    const photoId = this.#createPhotoId();

    this.state.upload = {
      file,
      fileName: file.name,
      photoId,
      mimeType: file.type || '',
      previewUrl: localObjectUrl,
      uploadedUrl: '',
      localObjectUrl,
      warning: '',
      warningCode: '',
      error: '',
      errorCode: '',
      isUploading: false,
      uploadFailed: false,
    };

    this.#render();

    const warning = await this.#measureUploadQuality(localObjectUrl);
    this.state.upload.warning = warning?.message || '';
    this.state.upload.warningCode = warning?.code || '';

    if (warning?.code) {
      this.#trackBlocker('validation', warning.code, {
        field_name: 'upload',
        step_name: 'upload',
        step_number: 4,
      });
    }

    if (this.config.upload?.endpoint) {
      this.state.upload.isUploading = true;
      this.#render();

      try {
        const uploadedUrl = await this.#uploadPhoto(file, photoId);
        this.state.upload.uploadedUrl = uploadedUrl;
      } catch (error) {
        console.error(error);
        this.state.upload.uploadFailed = true;
        this.state.upload.errorCode = 'upload_failed';
      } finally {
        this.state.upload.isUploading = false;
      }
    }

    this.#render();

    const uploadSignature = [file.name, file.size, file.lastModified].join(':');
    if (!this.state.upload.uploadFailed && this.trackingState.lastUploadedSignature !== uploadSignature) {
      this.trackingState.lastUploadedSignature = uploadSignature;
      this.#trackEvent('photo_uploaded', {
        step_name: 'upload',
        step_number: 4,
      });
    }
  }

  #validateFile(file) {
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'heic', 'heif'];
    const allowedMimeTypes = ['image/jpeg', 'image/png', 'image/heic', 'image/heif'];
    const normalizedMimeType = String(file.type || '').toLowerCase();
    const hasAllowedExtension = allowedExtensions.includes(extension);
    const hasAllowedMimeType = !normalizedMimeType || allowedMimeTypes.includes(normalizedMimeType);

    if (!hasAllowedExtension || !hasAllowedMimeType) {
      return {
        code: 'invalid_file_type',
        message: this.#getCopy('uploadInvalidTypeErrorText', 'Please upload a JPG, PNG, HEIC, or HEIF image'),
      };
    }

    if (file.size > 10 * 1024 * 1024) {
      return {
        code: 'file_too_large',
        message: this.#getCopy('uploadTooLargeErrorText', 'This file is too large — please upload a smaller image'),
      };
    }

    return null;
  }

  #beginStepEdit(editStepIndex) {
    if (!Number.isInteger(editStepIndex) || editStepIndex < 0 || editStepIndex >= STEPS.length) return;

    if (editStepIndex < this.activeStepIndex) {
      this.resumeStepIndex = this.activeStepIndex;
    } else if (this.resumeStepIndex != null && editStepIndex >= this.resumeStepIndex) {
      this.resumeStepIndex = null;
    }

    this.activeStepIndex = editStepIndex;
  }

  #advanceToNextStep() {
    const fallbackStepIndex = Math.min(this.activeStepIndex + 1, STEPS.length - 1);
    const nextStepIndex = this.#getResumeStepIndex(fallbackStepIndex);

    this.visibleStepCount = Math.max(this.visibleStepCount, nextStepIndex + 1);
    this.activeStepIndex = nextStepIndex;

    if (this.resumeStepIndex != null && nextStepIndex >= this.resumeStepIndex) {
      this.resumeStepIndex = null;
    }
  }

  #getResumeStepIndex(fallbackStepIndex) {
    if (this.resumeStepIndex == null || this.resumeStepIndex <= this.activeStepIndex) {
      return fallbackStepIndex;
    }

    for (let index = this.activeStepIndex + 1; index <= this.resumeStepIndex; index += 1) {
      const stepKey = STEPS[index];
      if (!this.state.completedSteps.has(stepKey)) {
        return index;
      }
    }

    return this.resumeStepIndex;
  }

  async #measureUploadQuality(objectUrl) {
    try {
      const dimensions = await this.#loadImageDimensions(objectUrl);
      if (!dimensions) return null;

      const shortestSide = Math.min(dimensions.width, dimensions.height);
      if (shortestSide < 1000) {
        return {
          code: 'low_quality_image',
          message: this.#getCopy(
            'uploadLowQualityWarningText',
            'This photo may be too low quality for printing — please upload a clearer image'
          ),
        };
      }
    } catch (error) {
      console.warn('Unable to measure upload dimensions', error);
    }

    return null;
  }

  #loadImageDimensions(objectUrl) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
      image.onerror = reject;
      image.src = objectUrl;
    });
  }

  async #uploadPhoto(file, photoId) {
    const formData = new FormData();
    const fieldName = this.config.upload?.fileFieldName || 'file';
    formData.append(fieldName, file);
    formData.append('photo_id', photoId);
    formData.append('product_id', String(this.config.productId));
    formData.append('product_handle', String(this.config.productHandle || ''));
    formData.append('section_id', String(this.config.sectionId));

    /*
      Merchant-specific integration hook:
      Replace this endpoint with your app proxy or upload service.
      The response should include a URL-like value accessible via upload_response_url_key.
    */
    const response = await fetch(this.config.upload.endpoint, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Photo upload failed with status ${response.status}`);
    }

    const responseJson = await response.json();
    const url = this.#readObjectPath(responseJson, this.config.upload?.responseUrlKey || 'url');

    if (!url || typeof url !== 'string') {
      throw new Error('Upload endpoint did not return a usable file URL');
    }

    return url;
  }

  async #submit() {
    const selectedVariant = this.#getSelectedVariant();

    if (!selectedVariant || !selectedVariant.available) {
      this.#setSubmitError('This combination is currently unavailable. Please choose another option.');
      this.#trackBlocker('product_configuration', 'variant_unavailable', {
        step_name: 'upload',
        step_number: 4,
      });
      return;
    }

    this.isSubmitting = true;
    this.#clearSubmitError();
    this.#clearError('upload');
    this.#setButtonLoading(true);
    this.#clearTrackedBlockers((blockerKey) => blockerKey.includes('checkout_submission|'));

    if (!this.trackingState.addToCartClicked) {
      this.trackingState.addToCartClicked = true;
      this.#trackEvent('customizer_add_to_cart_clicked', {
        ...this.#getAddToCartTrackingPayload(selectedVariant),
        step_name: 'upload',
        step_number: 4,
      });
    }

    const sections = this.#collectCartSections();
    const addUrl = this.#getCartAddUrl();
    const changeUrl = this.#getCartChangeUrl();
    const updateUrl = this.#getCartUpdateUrl();
    const cartUrl = this.#getCartBaseUrl();
    const properties = this.#buildLineItemProperties();

    try {
      /*
        Shopify's direct cart permalinks are not a reliable fit here because this flow needs
        line item properties for the portrait details and uploaded photo reference.
        The most reliable storefront-compatible approach is:
        1. add the configured portrait to cart with Ajax
        2. redirect to the branded cart review page for add-ons and confidence-building
      */
      if (this.prefill?.isEditing && this.prefill.cartItemKey) {
        await this.#updateEditedCartLine({
          selectedVariant,
          properties,
          addUrl,
          changeUrl,
          updateUrl,
          sections,
        });
      } else {
        await this.#requestCart(addUrl, {
          items: [
            {
              id: selectedVariant.id,
              quantity: 1,
              properties,
            },
          ],
        }, sections);
      }

      if (!this.trackingState.customizerCompleted) {
        this.trackingState.customizerCompleted = true;
        this.#trackEvent('customizer_completed', {
          step_name: 'upload',
          step_number: 4,
        });
      }

      this.#announce('Redirecting to cart review');
      window.location.assign(cartUrl);
    } catch (error) {
      console.error('[ProductCustomizer] Cart submission failed', error);
      this.#setSubmitError(error instanceof Error ? error.message : 'Unable to add this portrait to the cart.');
      const checkoutBlocker = this.#getCheckoutBlockerDetails(error);
      this.#trackBlocker('checkout_submission', checkoutBlocker.errorCode, {
        step_name: 'upload',
        step_number: 4,
        error_message_short: checkoutBlocker.errorMessageShort,
      });
      this.trackingState.addToCartClicked = false;
    } finally {
      this.isSubmitting = false;
      this.#setButtonLoading(false);
    }
  }

  #buildLineItemProperties() {
    const frame = this.catalog.choiceMaps.frame[this.state.selections.frame];
    const size = this.catalog.choiceMaps.size[this.state.selections.size];
    const background = this.catalog.choiceMaps.background[this.state.selections.background];
    const photoReference = this.state.upload.uploadedUrl || this.state.upload.fileName;
    const photoId = this.state.upload.photoId || '';
    const customizationId = this.prefill?.customizationId || photoId || this.#createPhotoId();

    return {
      'Dog name': this.state.name.trim(),
      Background: background.label,
      Frame: frame.label,
      Size: size.label,
      Photo: 'Uploaded',
      'Photo ID': photoId,
      _photo_reference: photoReference,
      _photo_preview: photoReference,
      _upload_status: this.state.upload.uploadedUrl
        ? 'uploaded'
        : 'local',
      _tf_line_type: 'portrait',
      _tf_customization_id: customizationId,
    };
  }

  async #updateEditedCartLine({ selectedVariant, properties, addUrl, changeUrl, updateUrl, sections }) {
    const cartItemKey = this.prefill.cartItemKey;
    const originalVariantId = Number(this.prefill.cartVariantId || 0);
    const selectedVariantId = Number(selectedVariant.id);

    if (originalVariantId && selectedVariantId !== originalVariantId) {
      await this.#requestCart(addUrl, {
        items: [
          {
            id: selectedVariant.id,
            quantity: 1,
            properties,
          },
        ],
      }, sections);

      await this.#requestCart(updateUrl, {
        updates: {
          [cartItemKey]: 0,
        },
      }, sections);
      return;
    }

    await this.#requestCart(changeUrl, {
      id: cartItemKey,
      quantity: 1,
      properties,
    }, sections);
  }

  async #requestCart(url, payload, sections = []) {
    const requestPayload = { ...payload };

    if (sections.length > 0) {
      requestPayload.sections = sections;
      requestPayload.sections_url = this.#getSectionsUrl();
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(requestPayload),
    });

    const responseJson = await this.#readJsonResponse(response);

    if (!response.ok || responseJson.status) {
      throw new Error(responseJson.description || responseJson.message || 'Unable to update this portrait in the cart.');
    }

    return responseJson;
  }

  #collectCartSections() {
    return [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((element) => element.getAttribute('data-section-id'))
      .filter(Boolean);
  }

  #getCartBaseUrl() {
    const configuredCartUrl = typeof this.config.cartUrl === 'string' ? this.config.cartUrl.trim() : '';
    const fallbackRoot = window.Shopify?.routes?.root || '/';
    const baseUrl = configuredCartUrl || `${fallbackRoot}cart`;
    return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  }

  #getCartAddUrl() {
    return `${this.#getCartBaseUrl()}/add.js`;
  }

  #getCartChangeUrl() {
    return `${this.#getCartBaseUrl()}/change.js`;
  }

  #getCartUpdateUrl() {
    return `${this.#getCartBaseUrl()}/update.js`;
  }

  #getCheckoutUrl() {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}checkout`;
  }

  #getSectionsUrl() {
    return `${window.location.pathname}${window.location.search}`;
  }

  async #readJsonResponse(response) {
    const responseText = await response.text();

    if (!responseText) {
      return {};
    }

    try {
      return JSON.parse(responseText);
    } catch (error) {
      console.error('[ProductCustomizer] Failed to parse Ajax cart response as JSON', {
        status: response.status,
        statusText: response.statusText,
        body: responseText,
      });
      throw new Error(`Cart request failed with an unexpected response (${response.status} ${response.statusText}).`);
    }
  }

  #getSelectedAddOns() {
    return Object.entries(this.state.addOns)
      .filter(([, selected]) => selected)
      .map(([key]) => this.catalog.addOns[key]);
  }

  #renderAddOnFreeShippingBadges(total, freeShippingThreshold, launchOfferIncludesFreeShipping = false) {
    this.dom.addonFreeShippingBadges.forEach((badge) => {
      const key = badge.getAttribute('data-addon-free-shipping-badge');
      if (!key || !this.catalog.addOns[key]) return;

      if (launchOfferIncludesFreeShipping) {
        badge.hidden = true;
        return;
      }

      const isSelected = Boolean(this.state.addOns[key]);
      const addOnPrice = this.catalog.addOns[key].price;
      const totalAfterAddOn = total + addOnPrice;
      const shouldShow =
        freeShippingThreshold > 0 &&
        total < freeShippingThreshold &&
        totalAfterAddOn >= freeShippingThreshold &&
        !isSelected;

      badge.hidden = !shouldShow;
    });
  }

  #hasExistingUpload() {
    return Boolean(this.state.upload.file || this.state.upload.previewUrl || this.state.upload.uploadedUrl);
  }

  #getSelectedVariant() {
    const sizeKey = this.state.selections.size || '12x16';
    const frameKey = this.state.selections.frame || 'black';
    const backgroundKey = this.state.selections.background || 'light';

    return this.catalog.variants.find(
      (variant) =>
        variant.sizeKey === sizeKey &&
        variant.frameKey === frameKey &&
        variant.backgroundKey === backgroundKey
    );
  }

  #previewVariantScore(variant) {
    let score = 0;
    if (variant.available) score += 100;
    if (variant.sizeKey === '12x16') score += 10;
    if (variant.image?.src) score += 5;
    return score;
  }

  #toCanonicalKey(type, value) {
    const normalizedValue = this.#normalize(value || '');
    const definition = CANONICAL_OPTIONS[type].find((item) => this.#matchesAliases(normalizedValue, item.aliases, true));
    return definition?.key || normalizedValue;
  }

  #matchesAliases(value, aliases, isNormalized = false) {
    const normalizedValue = isNormalized ? value : this.#normalize(value || '');
    return aliases.some((alias) => this.#normalize(alias) === normalizedValue);
  }

  #resolveSelectionKey(type, candidate, fallbackKey) {
    const choiceMap = this.catalog.choiceMaps[type] || {};

    if (candidate && choiceMap[candidate]) {
      return candidate;
    }

    if (fallbackKey && choiceMap[fallbackKey]) {
      return fallbackKey;
    }

    return Object.keys(choiceMap)[0] || fallbackKey || candidate || '';
  }

  #normalize(value) {
    return String(value)
      .toLowerCase()
      .replace(/×/g, 'x')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  #render() {
    this.#renderHeaderIntro();
    this.#renderSteps();
    this.#renderProgress();
    this.#renderOptions();
    this.#renderSummaries();
    this.#renderUploadHeading();
    this.#renderUploadState();
    this.#renderPreviewImage();
    this.#renderTotals();
    this.#renderAddOnPrices();
    this.#renderSubmitButton();
  }

  #renderHeaderIntro() {
    const isFirstStep = this.activeStepIndex === 0;

    if (this.dom.introHeading instanceof HTMLElement) {
      this.dom.introHeading.hidden = !isFirstStep;
    }
  }

  #renderSteps() {
    this.dom.steps.forEach((step, index) => {
      const stepKey = step.dataset.stepKey;
      if (!stepKey) return;

      const isVisible = index < this.visibleStepCount;
      const isActive = index === this.activeStepIndex;
      const isComplete = this.state.completedSteps.has(stepKey);
      const summary = step.querySelector('[data-step-summary]');

      step.hidden = !isVisible;
      step.classList.toggle('is-collapsed', isComplete && !isActive);

      if (summary instanceof HTMLElement) {
        summary.hidden = !(isComplete && !isActive);
      }
    });
  }

  #renderProgress() {
    const currentStepNumber = this.activeStepIndex + 1;
    if (this.dom.progressLabel) {
      this.dom.progressLabel.textContent = `Step ${currentStepNumber} of ${STEPS.length}`;
    }

    this.dom.progressDots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === this.activeStepIndex);
      dot.classList.toggle('is-complete', index < this.activeStepIndex);
    });
  }

  #renderOptions() {
    this.dom.optionButtons.forEach((button) => {
      const group = button.dataset.optionGroup;
      const key = button.dataset.optionKey;
      if (!group || !key) return;

      const isSelected = this.state.selections[group] === key;
      button.setAttribute('aria-checked', String(isSelected));

      if (group === 'size') {
        const choice = this.catalog.choiceMaps.size[key];
        const title = button.querySelector('[data-size-title]');
        const meta = button.querySelector('[data-size-meta]');

        if (choice && title instanceof HTMLElement) {
          title.textContent = choice.label;
        }

        if (choice && meta instanceof HTMLElement) {
          meta.textContent = choice.secondary;
        }
      }
    });

    this.dom.addonButtons.forEach((button) => {
      const key = button.dataset.addonKey;
      if (!key) return;
      button.setAttribute('aria-pressed', String(Boolean(this.state.addOns[key])));
    });

    const frameMessage = this.querySelector('[data-frame-message]');
    if (frameMessage) {
      frameMessage.textContent =
        this.state.selections.frame === 'print-only'
          ? this.#getCopy('framePrintOnlyHelperText', 'Frame recommended for best results')
          : this.#getCopy('frameReadyHelperText', 'Ready to hang — perfect for gifting');
    }

    this.dom.sizePrices.forEach((element) => {
      const sizeKey = element.getAttribute('data-size-price');
      if (!sizeKey) return;

      const price = this.#getVariantPriceForSize(sizeKey);
      element.textContent = price ? this.#formatMoney(price) : 'Unavailable';
    });
  }

  #renderSummaries() {
    const frame = this.catalog.choiceMaps.frame[this.state.selections.frame] || this.catalog.choiceMaps.frame.black;
    const size = this.catalog.choiceMaps.size[this.state.selections.size] || this.catalog.choiceMaps.size['12x16'];
    const background =
      this.catalog.choiceMaps.background[this.state.selections.background] || this.catalog.choiceMaps.background.light;

    if (this.dom.summaryValues.name) {
      this.dom.summaryValues.name.textContent = this.state.name.trim() || 'Not added yet';
    }

    if (this.dom.summaryValues.background) {
      this.dom.summaryValues.background.textContent = background.label;
    }

    if (this.dom.summaryValues.frameSize) {
      this.dom.summaryValues.frameSize.textContent = `${frame.label} · ${size.label}`;
    }

    if (this.dom.summaryBadges?.framePopular instanceof HTMLElement) {
      const popularFrame = this.config.popularTargets?.frame || '';
      const popularSize = this.config.popularTargets?.size || '';
      const isMostPopular =
        Boolean(popularFrame) &&
        Boolean(popularSize) &&
        this.state.selections.frame === popularFrame &&
        this.state.selections.size === popularSize;
      this.dom.summaryBadges.framePopular.hidden = !isMostPopular;
      this.dom.summaryBadges.framePopular.textContent = this.#getCopy('mostPopularTag', 'Most popular');
    }

    if (this.dom.summaryValues.upload) {
      this.dom.summaryValues.upload.textContent = this.#getUploadSummaryText();
    }

    if (this.dom.summaryValues.addons) {
      const labels = this.#getSelectedAddOns().map((item) => item.label);
      this.dom.summaryValues.addons.textContent =
        labels.length ? labels.join(' · ') : this.#getCopy('addonsNoneSelectedText', 'No add-ons selected');
    }

    if (this.dom.summarySwatches?.background instanceof HTMLElement) {
      this.dom.summarySwatches.background.style.backgroundColor =
        this.state.selections.background === 'dark' ? 'rgb(var(--color-foreground-rgb) / 0.85)' : 'rgb(var(--color-background-rgb) / 0.9)';
    }

    if (this.dom.summarySwatches?.frame instanceof HTMLElement) {
      const frameColors = {
        black: '#2B2B2B',
        white: '#F5F5F5',
        natural: '#D6B98C',
        'dark-brown': '#8B5E3C',
        'print-only': 'transparent',
      };

      const isPrintOnly = this.state.selections.frame === 'print-only';

      this.dom.summarySwatches.frame.hidden = isPrintOnly;
      this.dom.summarySwatches.frame.style.backgroundColor = frameColors[this.state.selections.frame] || 'transparent';
      this.dom.summarySwatches.frame.style.borderColor = 'rgb(var(--color-foreground-rgb) / 0.12)';
      this.dom.summarySwatches.frame.style.boxShadow = 'none';
    }

  }

  #renderUploadHeading() {
    if (!this.dom.uploadHeading) return;
    const name = this.state.name.trim();
    this.dom.uploadHeading.textContent = name ? `Upload ${name}’s photo` : 'Upload your dog’s photo';

    if (this.dom.uploadHeadingStatus instanceof HTMLElement) {
      this.dom.uploadHeadingStatus.hidden = true;
      this.dom.uploadHeadingStatus.classList.add('is-hidden');
      this.dom.uploadHeadingStatus.setAttribute('aria-hidden', 'true');
    }
  }

  #renderUploadState() {
    const hasUpload = Boolean(this.state.upload.previewUrl);
    const canPreviewImage = hasUpload && this.#canPreviewUploadImage();
    const uploadStatusMessage = this.#getUploadActiveLineText();
    const uploadFailureMessage = this.#getCopy('uploadSummaryFailedText', 'Upload failed. Please try again.');

    if (this.dom.uploadEmpty instanceof HTMLElement) {
      this.dom.uploadEmpty.hidden = hasUpload;
    }

    if (this.dom.uploadFilled instanceof HTMLElement) {
      this.dom.uploadFilled.hidden = !hasUpload;
    }

    if (this.dom.uploadPreviewFallback instanceof HTMLElement) {
      this.dom.uploadPreviewFallback.hidden = !hasUpload || canPreviewImage;
    }

    if (this.dom.uploadPreviewImage instanceof HTMLImageElement) {
      this.dom.uploadPreviewImage.hidden = !hasUpload || !canPreviewImage;

      if (hasUpload && canPreviewImage) {
        this.dom.uploadPreviewImage.src = this.state.upload.previewUrl;
      } else {
        this.dom.uploadPreviewImage.removeAttribute('src');
      }
    }

    if (this.dom.uploadFileName) {
      this.dom.uploadFileName.textContent = uploadStatusMessage;
    }

    if (this.dom.uploadWarning instanceof HTMLElement) {
      const hasWarning = Boolean(this.state.upload.warning);
      this.dom.uploadWarning.textContent = this.state.upload.warning;
      this.dom.uploadWarning.hidden = !hasWarning;
    }

    if (this.state.upload.error) {
      this.#setError('upload', this.state.upload.error, this.state.upload.errorCode || 'invalid_upload', 'upload', false);
    } else if (this.state.upload.uploadFailed) {
      this.#setError('upload', uploadFailureMessage, 'upload_failed', 'upload', false);
    } else {
      this.#clearError('upload');
    }
  }

  #getUploadSummaryText() {
    if (this.state.upload.uploadFailed) {
      return this.#getCopy('uploadSummaryFailedText', 'Upload failed. Please try again.');
    }

    if (this.state.upload.isUploading) {
      return this.#getCopy('uploadSummaryUploadingText', 'Uploading photo…');
    }

    if (this.state.upload.previewUrl) {
      return this.#getCopy('uploadSummaryUploadedText', 'Photo Uploaded');
    }

    return this.#getCopy('uploadSummaryDefaultText', 'Upload your photo');
  }

  #getUploadActiveLineText() {
    if (this.state.upload.uploadFailed) {
      return this.#getCopy('uploadSummaryFailedText', 'Upload failed. Please try again.');
    }

    if (this.state.upload.isUploading) {
      return this.#getCopy('uploadSummaryUploadingText', 'Uploading photo…');
    }

    if (this.state.upload.previewUrl) {
      return this.#getCopy('uploadSummaryUploadedText', 'Photo Uploaded');
    }

    return this.#getCopy('uploadSummaryDefaultText', 'Upload your photo');
  }

  #canPreviewUploadImage() {
    const mimeType = String(this.state.upload.mimeType || '').toLowerCase();
    if (mimeType === 'image/heic' || mimeType === 'image/heif') {
      return false;
    }

    const extensionSource = this.state.upload.fileName || this.state.upload.uploadedUrl || this.state.upload.previewUrl || '';
    const extension = extensionSource.split('.').pop()?.split('?')[0]?.toLowerCase() || '';

    return extension !== 'heic' && extension !== 'heif';
  }

  #renderPreviewImage() {
    if (!(this.dom.previewImage instanceof HTMLImageElement)) return;
    if (!this.#shouldUseVariantPreview()) return;

    const previewVariant =
      this.catalog.previewMap[`${this.state.selections.frame}::${this.state.selections.background}`] || this.#getSelectedVariant();
    const nextSrc = previewVariant?.image?.src;
    const nextSrcset = previewVariant?.image?.srcset || '';
    const nextSizes = previewVariant?.image?.sizes || this.dom.previewImage.getAttribute('sizes') || '';

    if (!nextSrc || this.currentPreviewUrl === nextSrc) return;
    this.currentPreviewUrl = nextSrc;

    if (prefersReducedMotion()) {
      if (nextSrcset) this.dom.previewImage.srcset = nextSrcset;
      if (nextSizes) this.dom.previewImage.sizes = nextSizes;
      this.dom.previewImage.src = nextSrc;
      return;
    }

    this.dom.previewImage.classList.add('is-swapping');
    const preload = new Image();
    preload.onload = () => {
      if (nextSrcset) this.dom.previewImage.srcset = nextSrcset;
      if (nextSizes) this.dom.previewImage.sizes = nextSizes;
      this.dom.previewImage.src = nextSrc;
      requestAnimationFrame(() => this.dom.previewImage.classList.remove('is-swapping'));
    };
    preload.src = nextSrc;
  }

  #shouldUseVariantPreview() {
    return this.hasPreviewVariantInteraction || this.activeStepIndex > 0;
  }

  #renderTotals() {
    const pricing = this.#getSelectedPricing();
    const total = pricing.price;
    const formattedTotal = this.#formatMoney(total);
    const selectionLabel = this.#getSelectionLabel();
    const launchOfferIncludesFreeShipping =
      Boolean(this.config.launchOfferIncludesFreeShipping) && this.#getLaunchDiscountPercent() > 0;

    if (this.dom.totalPrice) {
      this.dom.totalPrice.textContent = formattedTotal;
    }

    if (this.dom.currentSelectionPrice) {
      this.dom.currentSelectionPrice.textContent = formattedTotal;
    }

    if (this.dom.currentSelectionConfig) {
      this.dom.currentSelectionConfig.textContent = selectionLabel;
    }

    if (this.dom.stickySelectionConfig) {
      this.dom.stickySelectionConfig.textContent = selectionLabel;
    }

    this.#renderLaunchOffer(
      this.dom.currentSelectionOffer,
      this.dom.currentSelectionComparePrice,
      this.dom.currentSelectionSavings,
      pricing
    );

    this.#renderLaunchOffer(
      this.dom.stickySelectionOffer,
      this.dom.stickySelectionComparePrice,
      this.dom.stickySelectionSavings,
      pricing
    );

    const freeShippingThreshold = this.#getFreeShippingThreshold();
    const remaining = freeShippingThreshold - total;

    if (this.dom.shippingPrimary) {
      if (remaining > 0 && !launchOfferIncludesFreeShipping) {
        this.dom.shippingPrimary.innerHTML = `Add ${this.#formatMoney(remaining)} more for <span class="product-customizer__shipping-emphasis">FREE shipping</span>`;
        this.dom.shippingPrimary.classList.remove('is-success');
      } else {
        this.dom.shippingPrimary.textContent = '✓ You’ve unlocked FREE shipping';
        this.dom.shippingPrimary.classList.add('is-success');
      }
    }

    if (this.dom.shippingSecondary) {
      this.dom.shippingSecondary.hidden = true;
      this.dom.shippingSecondary.textContent = '';
    }

    this.#renderAddOnFreeShippingBadges(total, freeShippingThreshold, launchOfferIncludesFreeShipping);
  }

  #getSelectedPricing() {
    const selectedVariant = this.#getSelectedVariant();
    const basePrice = selectedVariant?.price || 0;
    const baseCompareAt = selectedVariant?.compareAtPrice || 0;
    const selectedAddOns = this.#getSelectedAddOns();
    const hasBaseCompareAtPrice = baseCompareAt > basePrice;
    const launchDiscountPercent = hasBaseCompareAtPrice ? 0 : this.#getLaunchDiscountPercent();
    const discountedBasePrice =
      launchDiscountPercent > 0 ? Math.round(basePrice * ((100 - launchDiscountPercent) / 100)) : basePrice;

    const addOnPrice = selectedAddOns.reduce((total, item) => total + item.price, 0);
    const addOnCompareAt = selectedAddOns.reduce((total, item) => {
      const compareAtPrice = item.compareAtPrice > item.price ? item.compareAtPrice : item.price;
      return total + compareAtPrice;
    }, 0);

    const price = discountedBasePrice + addOnPrice;
    const compareAtBasePrice = hasBaseCompareAtPrice || launchDiscountPercent > 0 ? Math.max(baseCompareAt, basePrice) : basePrice;
    const compareAtPrice = compareAtBasePrice + addOnCompareAt;
    const hasCompareAtPrice = compareAtPrice > price;

    return {
      price,
      compareAtPrice,
      savings: hasCompareAtPrice ? compareAtPrice - price : 0,
      hasCompareAtPrice,
    };
  }

  #getLaunchDiscountPercent() {
    const launchDiscountPercent = Number(this.config.launchPreviewDiscountPercent || 0);

    if (!Number.isFinite(launchDiscountPercent) || launchDiscountPercent <= 0 || launchDiscountPercent >= 100) {
      return 0;
    }

    return launchDiscountPercent;
  }

  #renderLaunchOffer(row, compareAtElement, savingsElement, pricing) {
    if (!(row instanceof HTMLElement)) return;

    row.hidden = !pricing.hasCompareAtPrice;

    if (!pricing.hasCompareAtPrice) {
      if (compareAtElement) compareAtElement.textContent = '';
      if (savingsElement) savingsElement.textContent = '';
      return;
    }

    if (compareAtElement) {
      compareAtElement.textContent = this.#formatMoney(pricing.compareAtPrice);
    }

    if (savingsElement) {
      savingsElement.textContent = `Save ${this.#formatMoney(pricing.savings)} today`;
    }
  }

  #renderAddOnPrices() {
    this.dom.addonPrices.forEach((element) => {
      const key = element.getAttribute('data-addon-price');
      if (!key || !this.catalog.addOns[key]) return;
      element.textContent = this.#formatMoney(this.catalog.addOns[key].price);
    });
  }

  #renderSubmitButton() {
    if (!this.dom.ctaButton) return;
    this.dom.ctaButton.textContent = this.activeStepIndex === STEPS.length - 1 ? 'Create My Portrait' : 'Continue';
  }

  #getVariantPriceForSize(sizeKey) {
    const frameKey = this.state.selections.frame || 'black';
    const backgroundKey = this.state.selections.background || 'light';

    const exactMatch = this.catalog.variants.find(
      (variant) =>
        variant.sizeKey === sizeKey &&
        variant.frameKey === frameKey &&
        variant.backgroundKey === backgroundKey
    );

    if (exactMatch) return exactMatch.price;

    const fallbackMatch = this.catalog.variants.find(
      (variant) => variant.sizeKey === sizeKey && variant.frameKey === frameKey
    );

    return fallbackMatch?.price || 0;
  }

  #getSelectionLabel() {
    const frame = this.catalog.choiceMaps.frame[this.state.selections.frame] || this.catalog.choiceMaps.frame.black;
    const size = this.catalog.choiceMaps.size[this.state.selections.size] || this.catalog.choiceMaps.size['12x16'];
    return `${this.#getFrameDisplayLabel(frame)} · ${this.#getCompactSizeLabel(size)}`;
  }

  #getFrameDisplayLabel(frame) {
    const label = frame?.label || 'Black';
    if (this.#normalize(label) === 'print only') {
      return 'Print Only';
    }

    return `${label.replace(/\s+frame$/i, '')} Frame`;
  }

  #getCompactSizeLabel(size) {
    const label = size?.label || '12″ × 16″';
    return label
      .replace(/[“”"]/g, '″')
      .replace(/\s*[×x]\s*/i, '×')
      .replace(/″×/g, '×')
      .replace(/×([0-9]+)/, '×$1')
      .replace(/([0-9])$/, '$1″');
  }

  #formatMoney(amount) {
    return formatMoney(amount, this.config.moneyFormat, this.config.currencyCode);
  }

  #getFreeShippingThreshold() {
    const thresholds = this.config.freeShippingThresholds || {};
    const currencyCode = String(this.config.currencyCode || 'GBP').toUpperCase();
    const gbpThreshold = Number(thresholds.GBP || 0);
    const selectedThreshold = Number(thresholds[currencyCode]);

    if (Number.isFinite(selectedThreshold) && selectedThreshold > 0) {
      return selectedThreshold;
    }

    if (Number.isFinite(gbpThreshold) && gbpThreshold > 0) {
      return gbpThreshold;
    }

    return 0;
  }

  #setError(field, message, errorType, stepName, track = true) {
    const element = this.dom.errors[field];
    if (!element) return;

    element.textContent = message;
    element.hidden = false;

    if (field === 'name') {
      this.dom.nameInput?.classList.add('is-error');
    }

    if (field === 'upload') {
      this.dom.uploadTrigger?.classList.add('is-error');
    }

    if (track) {
      const stepNumber = this.#getStepNumber(stepName);
      this.#trackBlocker('validation', errorType, {
        field_name: field,
        step_name: stepName,
        step_number: stepNumber || undefined,
      });
    }
  }

  #clearError(field) {
    const element = this.dom.errors[field];
    if (element) {
      element.textContent = '';
      element.hidden = true;
    }

    if (field === 'name') {
      this.dom.nameInput?.classList.remove('is-error');
    }

    if (field === 'upload') {
      this.dom.uploadTrigger?.classList.remove('is-error');
    }

    this.#clearTrackedBlockers((blockerKey) => blockerKey.startsWith(`validation|`) && blockerKey.includes(`|${field}|`));
  }

  #setSubmitError(message) {
    if (!this.dom.submitError) return;
    this.dom.submitError.textContent = message;
    this.dom.submitError.hidden = false;
    this.#announce(message);
  }

  #clearSubmitError() {
    if (!this.dom.submitError) return;
    this.dom.submitError.textContent = '';
    this.dom.submitError.hidden = true;
  }

  #setButtonLoading(isLoading) {
    if (!this.dom.ctaButton) return;
    this.dom.ctaButton.setAttribute('aria-busy', String(isLoading));
    this.dom.ctaButton.disabled = isLoading;
  }

  #announce(message) {
    if (this.dom.liveRegion) {
      this.dom.liveRegion.textContent = message;
    }
  }

  #scrollStepIntoView(index) {
    const step = this.dom.steps[index];
    if (!(step instanceof HTMLElement)) return;

    if (this.pendingStepScrollTimeout) {
      window.clearTimeout(this.pendingStepScrollTimeout);
      this.pendingStepScrollTimeout = null;
    }

    const runDesktopScroll = (behavior) => {
      const currentStep = this.dom.steps[index];
      if (!(currentStep instanceof HTMLElement)) return;

      const scrollTarget = currentStep.querySelector('.product-customizer-step__header') || currentStep;
      const scrollContainer = this.dom.scrollContainer;
      if (!(scrollContainer instanceof HTMLElement)) return;

      const containerRect = scrollContainer.getBoundingClientRect();
      const targetRect = scrollTarget.getBoundingClientRect();
      const panelOffset = 40;
      const targetTop =
        scrollContainer.scrollTop + (targetRect.top - containerRect.top) - panelOffset;

      scrollContainer.scrollTo({
        top: Math.max(0, targetTop),
        behavior,
      });
    };

    const runMobileScroll = (behavior) => {
      const currentStep = this.dom.steps[index];
      if (!(currentStep instanceof HTMLElement)) return;

      const scrollTarget = currentStep.querySelector('.product-customizer-step__header') || currentStep;
      const headerOffset = this.#getHeaderOffset();
      const viewportOffset = Math.round(window.innerHeight * 0.1);
      const targetTop = scrollTarget.getBoundingClientRect().top + window.scrollY - headerOffset - viewportOffset;

      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior,
      });
    };

    const delay = window.innerWidth >= 990 ? 80 : 220;

    this.pendingStepScrollTimeout = window.setTimeout(() => {
      this.pendingStepScrollTimeout = null;

      if (window.innerWidth >= 990) {
        const desktopBehavior = prefersReducedMotion() ? 'auto' : 'smooth';
        runDesktopScroll(desktopBehavior);
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => runDesktopScroll('auto'));
        });
        return;
      }

      runMobileScroll(prefersReducedMotion() ? 'auto' : 'smooth');
    }, delay);
  }

  #scrollElementIntoViewCentered(target) {
    if (!(target instanceof HTMLElement)) return;

    if (this.pendingStepScrollTimeout) {
      window.clearTimeout(this.pendingStepScrollTimeout);
      this.pendingStepScrollTimeout = null;
    }

    const runDesktopScroll = (behavior) => {
      const scrollContainer = this.dom.scrollContainer;
      if (!(scrollContainer instanceof HTMLElement)) return;

      const containerRect = scrollContainer.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const targetCenter = targetRect.top - containerRect.top + scrollContainer.scrollTop + targetRect.height / 2;
      const containerCenter = containerRect.height / 2;
      const nextTop = targetCenter - containerCenter;

      scrollContainer.scrollTo({
        top: Math.max(0, nextTop),
        behavior,
      });
    };

    const runMobileScroll = (behavior) => {
      const headerOffset = this.#getHeaderOffset();
      const targetRect = target.getBoundingClientRect();
      const targetCenter = targetRect.top + window.scrollY + targetRect.height / 2;
      const viewportCenter = window.innerHeight / 2;
      const nextTop = targetCenter - viewportCenter - headerOffset / 2;

      window.scrollTo({
        top: Math.max(0, nextTop),
        behavior,
      });
    };

    const delay = window.innerWidth >= 990 ? 80 : 140;

    this.pendingStepScrollTimeout = window.setTimeout(() => {
      this.pendingStepScrollTimeout = null;

      if (window.innerWidth >= 990) {
        const desktopBehavior = prefersReducedMotion() ? 'auto' : 'smooth';
        runDesktopScroll(desktopBehavior);
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => runDesktopScroll('auto'));
        });
        return;
      }

      runMobileScroll(prefersReducedMotion() ? 'auto' : 'smooth');
    }, delay);
  }

  #getHeaderOffset() {
    const rootStyles = window.getComputedStyle(document.documentElement);
    const headerHeight = parseFloat(rootStyles.getPropertyValue('--header-group-height')) || 0;
    return headerHeight;
  }

  /*
    Tracking helpers:
    Centralize customizer payload building so future steps and products can
    reuse the same event shape without touching the flow logic.
  */
  #trackEvent(eventName, payload = {}) {
    if (typeof window.tfTrack !== 'function') return null;
    return window.tfTrack(eventName, {
      ...this.#getTrackingContext(),
      ...payload,
    });
  }

  /*
    customizer_blocker_seen is the single blocker audit event.
    blocker_severity must always normalize to "soft" or "hard", and
    error_code values must stay stable, controlled, and free from PII.
  */
  #trackBlocker(blockerType, errorCode, payload = {}) {
    const blockerSeverity = this.#getBlockerSeverity(errorCode);
    const blockerKey = this.#buildBlockerDeduplicationKey(blockerType, errorCode, payload);

    if (this.trackingState.seenBlockers.has(blockerKey)) {
      return null;
    }

    this.trackingState.seenBlockers.add(blockerKey);

    return this.#trackEvent('customizer_blocker_seen', {
      blocker_type: blockerType,
      blocker_severity: blockerSeverity,
      error_code: errorCode,
      ...payload,
    });
  }

  #getBlockerSeverity(errorCode) {
    const hardBlockers = new Set([
      'required',
      'invalid_upload',
      'upload_failed',
      'invalid_file_type',
      'file_too_large',
      'upload_in_progress',
      'variant_unavailable',
      'addon_unavailable',
      'cart_submission_failed',
      'cart_api_error',
      'network_error',
      'unknown_checkout_error',
    ]);

    if (errorCode === 'low_quality_image') {
      return 'soft';
    }

    return hardBlockers.has(errorCode) ? 'hard' : 'soft';
  }

  #buildBlockerDeduplicationKey(blockerType, errorCode, payload = {}) {
    return [
      blockerType,
      errorCode,
      payload.step_name || '',
      payload.step_number || '',
      payload.field_name || '',
      this.state.selections.size || '',
      this.state.selections.frame || '',
      this.state.selections.background || '',
      Object.entries(this.state.addOns)
        .filter(([, selected]) => selected)
        .map(([key]) => key)
        .sort()
        .join(','),
    ].join('|');
  }

  #clearTrackedBlockers(predicate) {
    this.trackingState.seenBlockers.forEach((blockerKey) => {
      if (predicate(blockerKey)) {
        this.trackingState.seenBlockers.delete(blockerKey);
      }
    });
  }

  #getCheckoutBlockerDetails(error) {
    if (!(error instanceof Error)) {
      return {
        errorCode: 'unknown_checkout_error',
        errorMessageShort: 'Checkout could not be started.',
      };
    }

    if (error.name === 'TypeError') {
      return {
        errorCode: 'network_error',
        errorMessageShort: 'Network issue while starting checkout.',
      };
    }

    const message = String(error.message || '').toLowerCase();

    if (message.includes('cart request failed with an unexpected response')) {
      return {
        errorCode: 'cart_api_error',
        errorMessageShort: 'Cart service returned an unexpected response.',
      };
    }

    if (message.includes('unable to add this portrait to the cart')) {
      return {
        errorCode: 'cart_submission_failed',
        errorMessageShort: 'Could not add the portrait to the cart.',
      };
    }

    return {
      errorCode: 'unknown_checkout_error',
      errorMessageShort: 'Checkout could not be started.',
    };
  }

  #getStepNumber(stepName) {
    if (!stepName) return null;

    const stepIndex = STEPS.indexOf(stepName);
    return stepIndex >= 0 ? stepIndex + 1 : null;
  }

  #getTrackingContext() {
    const frame = this.catalog.choiceMaps.frame[this.state.selections.frame];
    const size = this.catalog.choiceMaps.size[this.state.selections.size];
    const background = this.catalog.choiceMaps.background[this.state.selections.background];

    return {
      customizer_type: this.config.customizerType || this.getAttribute('data-customizer-type') || undefined,
      customizer_version: this.config.customizerVersion || this.getAttribute('data-customizer-version') || undefined,
      product_handle: this.config.productHandle || undefined,
      selected_size: size?.label || undefined,
      selected_frame: frame?.label || undefined,
      selected_background: background?.label || undefined,
      addons_selected: this.#getSelectedAddOns().map((item) => item.key),
    };
  }

  #getAddToCartTrackingPayload(selectedVariant) {
    const currency = String(this.config.currencyCode || window.Shopify?.currency?.active || 'GBP').toUpperCase();
    const price = Number(selectedVariant?.price || 0) / 100;
    const quantity = 1;
    const itemId = selectedVariant?.id ? String(selectedVariant.id) : undefined;
    const itemName = this.config.productTitle || this.config.productHandle || 'Custom portrait';
    const cartAction = this.prefill?.isEditing && this.prefill.cartItemKey ? 'update_cart_item' : 'add_cart_item';

    return {
      event_role: 'customizer_add_to_cart',
      cart_action: cartAction,
      currency,
      value: price,
      item_count: quantity,
      variant_id: itemId,
      product_id: this.config.productId ? String(this.config.productId) : undefined,
      item_name: itemName,
      ecommerce: {
        currency,
        value: price,
        items: [
          {
            item_id: itemId,
            item_name: itemName,
            item_variant: selectedVariant?.title || undefined,
            item_category: this.config.productCategory || undefined,
            price,
            quantity,
            product_handle: this.config.productHandle || undefined,
            selected_size: selectedVariant?.sizeLabel || undefined,
            selected_frame: selectedVariant?.frameLabel || undefined,
            selected_background: selectedVariant?.backgroundLabel || undefined,
            cart_action: cartAction,
          },
        ],
      },
    };
  }

  #readObjectPath(object, path) {
    return String(path)
      .split('.')
      .reduce((result, key) => (result && key in result ? result[key] : undefined), object);
  }
  
  #createPhotoId() {
    const now = new Date();
    const dateStamp = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
    ].join('');
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const values = new Uint8Array(6);

    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      window.crypto.getRandomValues(values);
    }   else {
      values.forEach((_, index) => {
      values[index] = Math.floor(Math.random() * 256);
      });
    }

    const suffix = Array.from(values, (value) => alphabet[value % alphabet.length]).join('');
    return `TF-${dateStamp}-${suffix}`;
  }

  #revokePreviewObjectUrl() {
    if (this.state?.upload?.localObjectUrl) {
      URL.revokeObjectURL(this.state.upload.localObjectUrl);
    }
  }
}

if (!customElements.get('product-customizer-component')) {
  customElements.define('product-customizer-component', ProductCustomizerComponent);
}
